import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import {
  Question,
  GoogleLogo,
  AppleLogo,
  MicrosoftOutlookLogo,
  ArrowsClockwise,
  CheckCircle,
  WarningCircle,
  Lightning,
} from "@phosphor-icons/react";

/**
 * CalendarHowToDrawer — Pro-only in-dashboard help drawer covering the
 * two calendar integrations (Google Calendar 2-way sync + Apple/Outlook
 * iCal feed). Triggered by a small "?" button rendered alongside the
 * calendar cards. Keeps the dashboard itself uncluttered while giving
 * the owner a one-tap walkthrough whenever they need it.
 */
export default function CalendarHowToDrawer() {
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card hover:bg-muted/60 px-3 py-1.5 text-[12px] font-semibold text-muted-foreground hover:text-foreground transition"
          data-testid="calendar-howto-trigger"
        >
          <Question weight="bold" className="h-3.5 w-3.5" />
          How calendar sync works
        </button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto"
        data-testid="calendar-howto-drawer"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-xl tracking-tight">
            Sync your TruckTrackr schedule
          </SheetTitle>
          <SheetDescription className="text-sm leading-relaxed">
            Two ways to keep your phone&apos;s calendar and TruckTrackr in lockstep.
            Pick whichever fits your workflow — or use both.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-8 pb-12 pr-1">
          {/* Quick comparison */}
          <div className="rounded-xl border border-border bg-muted/30 p-4 text-[13px] leading-relaxed">
            <div className="font-semibold text-foreground mb-2 flex items-center gap-1.5">
              <Lightning weight="fill" className="h-4 w-4 text-primary" />
              Which one should I use?
            </div>
            <ul className="space-y-1.5 text-muted-foreground">
              <li>
                <strong className="text-foreground">Google Calendar:</strong>{" "}
                Best if Google Calendar is already where you plan your week.
                Two-way sync — edit either side, they stay aligned.
              </li>
              <li>
                <strong className="text-foreground">Outlook Calendar:</strong>{" "}
                Same full two-way sync, but for Outlook.com or Microsoft 365.
              </li>
              <li>
                <strong className="text-foreground">Apple / Outlook feed:</strong>{" "}
                Best if you live in Apple Calendar or just want a read-only
                view to share with employees or your accountant.
              </li>
            </ul>
          </div>

          {/* GCal section */}
          <section data-testid="howto-section-gcal">
            <div className="flex items-center gap-2 mb-3">
              <GoogleLogo weight="duotone" className="h-6 w-6 text-primary" />
              <h3 className="font-display font-semibold text-lg tracking-tight">
                Google Calendar (two-way sync)
              </h3>
            </div>
            <p className="text-[13px] text-muted-foreground mb-4 leading-relaxed">
              Connect once and every event you book in Google Calendar publishes
              to your TruckTrackr map. Edits you make on TruckTrackr push back up
              to Google so the two never drift apart.
            </p>

            <ol className="space-y-3">
              <Step n={1} title="Open the Google Calendar card on this dashboard">
                Scroll up to the &ldquo;Connect Google Calendar&rdquo; card.
              </Step>
              <Step
                n={2}
                title='Pick "Create a dedicated TruckTrackr Schedule calendar"'
              >
                Recommended. TruckTrackr creates a brand-new calendar inside
                your Google account so personal events never leak onto the map.
                Already have a calendar you want to use?{" "}
                <em>&ldquo;Pick an existing calendar&rdquo;</em> works too.
              </Step>
              <Step n={3} title="Sign in with Google and grant calendar access">
                Google takes you to its sign-in page. Approve the calendar
                permission and you&apos;ll bounce back here with a green
                &ldquo;Connected&rdquo; badge.
              </Step>
              <Step n={4} title="Add events with three required fields">
                Every event needs:{" "}
                <span className="font-semibold text-foreground">Title</span>,{" "}
                <span className="font-semibold text-foreground">
                  full street address in the Location field
                </span>{" "}
                (e.g. <em>&ldquo;550 S Reading Rd, Ephrata, PA 17522&rdquo;</em>),
                and a{" "}
                <span className="font-semibold text-foreground">
                  specific start and end time
                </span>{" "}
                (no all-day events). Anything missing lands in the &ldquo;Needs
                fix&rdquo; inbox on the Google Calendar card.
              </Step>
              <Step n={5} title="That's it — auto-syncs every 5 minutes">
                New events appear on the public map within 5 minutes. Delete an
                event in Google → it disappears from TruckTrackr. Edit on
                TruckTrackr → the Google event updates too.
              </Step>
            </ol>

            <Callout
              icon={<ArrowsClockwise weight="bold" className="h-4 w-4" />}
              tone="info"
              title="Two-way sync — what to know"
            >
              When the same event is edited in both places near-simultaneously,
              the most recent save wins. In practice you&apos;ll never notice —
              pick one app, work in it, and the other catches up within minutes.
            </Callout>

            <Callout
              icon={<WarningCircle weight="bold" className="h-4 w-4" />}
              tone="warn"
              title="Events stuck in the Needs-fix inbox?"
            >
              Open the event in Google, double-check the{" "}
              <strong>Location</strong> field (must include a street number AND
              a state or ZIP) and that it has{" "}
              <strong>specific start/end times</strong>. Save in Google, wait up
              to 5 min, and it&apos;ll publish automatically.
            </Callout>
          </section>

          {/* Apple section */}
          <section data-testid="howto-section-outlook">
            <div className="flex items-center gap-2 mb-3">
              <MicrosoftOutlookLogo weight="duotone" className="h-6 w-6 text-primary" />
              <h3 className="font-display font-semibold text-lg tracking-tight">
                Outlook Calendar (two-way sync)
              </h3>
            </div>
            <p className="text-[13px] text-muted-foreground mb-4 leading-relaxed">
              Works with both <strong className="text-foreground">personal Outlook.com</strong> and{" "}
              <strong className="text-foreground">Microsoft 365 work / school accounts</strong>. Same full two-way
              behavior as the Google integration — anything you create in Outlook publishes to TruckTrackr; anything
              you create on TruckTrackr lands in Outlook.
            </p>

            <ol className="space-y-3">
              <Step n={1} title="Open the Outlook Calendar card on this dashboard">
                Scroll up to the &ldquo;Connect Outlook Calendar&rdquo; card.
              </Step>
              <Step
                n={2}
                title='Pick "Create a dedicated TruckTrackr Schedule calendar"'
              >
                Recommended. TruckTrackr creates a brand-new calendar inside your Outlook account so personal events
                stay separate. Already have a calendar you want to use? &ldquo;Pick an existing calendar&rdquo; works too.
              </Step>
              <Step n={3} title="Sign in with Microsoft and grant calendar access">
                Microsoft takes you to its sign-in page (works with personal or Microsoft 365 accounts). Approve the
                Calendars.ReadWrite permission and you&apos;ll bounce back with a green &ldquo;Connected&rdquo; badge.
              </Step>
              <Step n={4} title="Add events with three required fields">
                Every event needs:{" "}
                <span className="font-semibold text-foreground">Subject</span>,{" "}
                <span className="font-semibold text-foreground">
                  full street address in the Location field
                </span>
                , and a{" "}
                <span className="font-semibold text-foreground">specific start and end time</span>{" "}
                (no all-day events). Anything missing lands in the &ldquo;Needs fix&rdquo; inbox on the Outlook card.
              </Step>
              <Step n={5} title="That's it — auto-syncs every 5 minutes">
                New events appear on the public map within 5 minutes. Delete in Outlook → it disappears from
                TruckTrackr. Edit on TruckTrackr → the Outlook event updates within seconds.
              </Step>
            </ol>

            <Callout
              icon={<ArrowsClockwise weight="bold" className="h-4 w-4" />}
              tone="info"
              title="Personal AND Microsoft 365 accounts"
            >
              Behind the scenes we use Microsoft Graph with the <code>/common/</code> sign-in endpoint, which accepts
              both consumer Outlook.com / Hotmail / Live accounts AND Microsoft 365 work or school accounts. One card,
              both audiences.
            </Callout>
          </section>

          {/* Apple section */}
          <section data-testid="howto-section-ical">
            <div className="flex items-center gap-2 mb-3">
              <AppleLogo weight="fill" className="h-6 w-6 text-primary" />
              <h3 className="font-display font-semibold text-lg tracking-tight">
                Apple Calendar / Outlook (read-only feed)
              </h3>
            </div>
            <p className="text-[13px] text-muted-foreground mb-4 leading-relaxed">
              Generate one URL that any calendar app can subscribe to —{" "}
              <strong className="text-foreground">read-only</strong>, refreshes
              every ~30 minutes. Your schedule shows up on your phone, your
              laptop, even your accountant&apos;s calendar if you share it. Edits
              made inside Apple Calendar will NOT come back into TruckTrackr —
              use the Google integration if you need that.
            </p>

            <ol className="space-y-3">
              <Step n={1} title="Generate your feed URL">
                On the &ldquo;Subscribe-able calendar feed&rdquo; card above,
                tap{" "}
                <span className="font-semibold text-foreground">
                  Generate my feed URL
                </span>
                .
              </Step>
              <Step n={2} title="Tap Copy and pick the app you want to subscribe in">
                The exact menu path differs slightly per app — see the
                cheat-sheet below.
              </Step>
            </ol>

            <div className="mt-5 space-y-3 text-[13px]">
              <AppRow
                icon={<AppleLogo weight="fill" className="h-4 w-4" />}
                title="iPhone / iPad"
              >
                Easiest: just tap the{" "}
                <em>&ldquo;Open in Apple Calendar&rdquo;</em> button on the feed
                card. iOS handles the subscribe dialog automatically. If that
                button doesn&apos;t work, open Settings → Calendar →{" "}
                <strong>Accounts → Add Account → Other → Add Subscribed
                Calendar</strong>, then paste the feed URL.
              </AppRow>
              <AppRow
                icon={<AppleLogo weight="fill" className="h-4 w-4" />}
                title="Mac (Apple Calendar)"
              >
                In Apple Calendar:{" "}
                <strong>File → New Calendar Subscription</strong>, paste the
                URL, click <strong>Subscribe</strong>, then set{" "}
                <em>Auto-refresh</em> to every hour or finer.
              </AppRow>
              <AppRow
                icon={
                  <MicrosoftOutlookLogo weight="fill" className="h-4 w-4" />
                }
                title="Outlook (web)"
              >
                <strong>Add calendar → Subscribe from web</strong> → paste the
                URL → name it &ldquo;TruckTrackr Schedule&rdquo; → Import.
              </AppRow>
              <AppRow
                icon={
                  <MicrosoftOutlookLogo weight="fill" className="h-4 w-4" />
                }
                title="Outlook (desktop)"
              >
                In the Calendar tab:{" "}
                <strong>
                  Open Calendar → From Internet → paste URL → OK
                </strong>
                .
              </AppRow>
              <AppRow
                icon={<GoogleLogo weight="duotone" className="h-4 w-4" />}
                title="Google Calendar"
              >
                On google.com/calendar, in the left sidebar click the{" "}
                <strong>+</strong> next to &ldquo;Other calendars&rdquo; →{" "}
                <strong>From URL</strong> → paste the feed URL →{" "}
                <strong>Add calendar</strong>.
              </AppRow>
            </div>

            <Callout
              icon={<CheckCircle weight="bold" className="h-4 w-4" />}
              tone="success"
              title="Pro tip: share with employees or partners"
            >
              The feed URL is just a link — you can paste it into a group chat,
              email it to your accountant, or post it on your business&apos;s
              internal wiki. Everyone you share it with sees the same up-to-date
              schedule. Want to cut them off later? Tap{" "}
              <strong>Rotate URL</strong> on the feed card.
            </Callout>

            <Callout
              icon={<WarningCircle weight="bold" className="h-4 w-4" />}
              tone="warn"
              title="The feed is read-only"
            >
              Apple Calendar / Outlook can&apos;t push edits back into
              TruckTrackr — that&apos;s an iCal spec limitation. If you want to
              edit your schedule from outside TruckTrackr, use the Google
              Calendar integration instead.
            </Callout>
          </section>

          {/* Closing FAQ */}
          <section className="border-t border-border pt-6">
            <h3 className="font-display font-semibold text-lg tracking-tight mb-3">
              Common questions
            </h3>
            <div className="space-y-3 text-[13px] leading-relaxed">
              <FAQ q="Can I use both at the same time?">
                Yes. Google Calendar two-way + Apple feed read-only is a common
                setup: Google is where you edit; Apple is where your team views.
              </FAQ>
              <FAQ q="How fast do edits show up?">
                Google → TruckTrackr: within 5 min. TruckTrackr → Google: within
                a few seconds. Apple/Outlook feed: refreshes every ~30 min
                depending on the app&apos;s polling.
              </FAQ>
              <FAQ q="What happens if I disconnect Google Calendar?">
                Any events that came from Google are removed from your
                TruckTrackr schedule. Events you posted manually inside
                TruckTrackr stay put. You can reconnect any time without losing
                history.
              </FAQ>
              <FAQ q="Will customers see my personal events?">
                No. Only events with a complete title, full address, and start
                + end time publish to the map — personal events without an
                address never appear. Use the dedicated calendar option for
                belt-and-suspenders separation.
              </FAQ>
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Step({ n, title, children }) {
  return (
    <li className="flex gap-3" data-testid={`howto-step-${n}`}>
      <span className="flex-none mt-0.5 grid place-items-center h-6 w-6 rounded-full bg-primary/15 text-primary text-[11px] font-bold">
        {n}
      </span>
      <div className="flex-1 text-[13px] leading-relaxed">
        <div className="font-semibold text-foreground mb-0.5">{title}</div>
        <div className="text-muted-foreground">{children}</div>
      </div>
    </li>
  );
}

function AppRow({ icon, title, children }) {
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2.5">
      <div className="flex items-center gap-1.5 font-semibold text-foreground mb-1 text-[13px]">
        {icon}
        {title}
      </div>
      <div className="text-muted-foreground text-[12.5px] leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function Callout({ icon, tone, title, children }) {
  const tones = {
    info: "border-primary/30 bg-primary/5 text-primary",
    warn: "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300",
    success:
      "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300",
  };
  return (
    <div className={`mt-4 rounded-lg border ${tones[tone] || tones.info} px-3 py-2.5`}>
      <div className="flex items-center gap-1.5 font-semibold text-[12.5px] mb-1">
        {icon}
        {title}
      </div>
      <div className="text-foreground/80 text-[12.5px] leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function FAQ({ q, children }) {
  return (
    <details className="rounded-lg border border-border bg-card px-3 py-2">
      <summary className="cursor-pointer font-semibold text-foreground hover:text-primary text-[13px]">
        {q}
      </summary>
      <div className="mt-2 text-muted-foreground">{children}</div>
    </details>
  );
}
